import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Check, CreditCard, Calendar, AlertCircle, ArrowRight,
  Crown, Building2, Sparkles, Download, ChevronRight
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const SubscriptionManagement = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/account/subscription');
      return;
    }
    
    fetchSubscription();
    fetchBillingHistory();
    
    // Check if returning from checkout
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [isAuthenticated]);

  const fetchSubscription = async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${API}/subscriptions/my-subscription`, { headers });
      setSubscription(res.data.subscription);
      setPlan(res.data.plan);
      setUsage(res.data.usage);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${API}/subscriptions/billing-history`, { headers });
      setBillingHistory(res.data.transactions || []);
    } catch (err) {
      console.error('Failed to fetch billing history:', err);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;
      const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { headers });
      
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        fetchSubscription();
      }
    } catch (err) {
      console.error('Failed to check payment status:', err);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will have access until the end of your billing period.')) {
      return;
    }

    try {
      setCancelling(true);
      const headers = await getAuthHeaders();
      await axios.post(`${API}/subscriptions/cancel`, {}, { headers });
      toast.success('Subscription cancelled successfully');
      fetchSubscription();
    } catch (err) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'pro': return <Crown className="w-6 h-6" />;
      case 'studio': return <Building2 className="w-6 h-6" />;
      default: return <Sparkles className="w-6 h-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const isFreePlan = plan?.plan_id === 'free';
  const isTrialing = subscription?.status === 'trialing';
  const isCancelled = subscription?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12 pt-28">
        <h1 className="text-3xl font-medium text-stone-900 mb-8">Subscription</h1>

        {/* Current Plan Card */}
        <div className="bg-white border border-stone-200 rounded-xl p-8 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                isFreePlan ? 'bg-stone-100 text-stone-600' : 'bg-primary/10 text-primary'
              }`}>
                {getPlanIcon(plan?.plan_id)}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-stone-900">{plan?.name} Plan</h2>
                <p className="text-stone-500">{plan?.description}</p>
              </div>
            </div>
            
            {/* Status Badge */}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isTrialing ? 'bg-[var(--gold)]/20 text-[var(--gold)]' :
              isCancelled ? 'bg-red-100 text-red-700' :
              isFreePlan ? 'bg-stone-100 text-stone-600' :
              'bg-green-100 text-green-700'
            }`}>
              {isTrialing ? 'Trial' :
               isCancelled ? 'Cancelled' :
               isFreePlan ? 'Free' :
               'Active'}
            </div>
          </div>

          {/* Subscription Details */}
          {!isFreePlan && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 p-4 bg-stone-50 rounded-lg">
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Billing Cycle</p>
                <p className="font-medium text-stone-900 capitalize">
                  {subscription?.billing_cycle || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">
                  {isTrialing ? 'Trial Ends' : isCancelled ? 'Access Until' : 'Next Billing'}
                </p>
                <p className="font-medium text-stone-900">
                  {formatDate(subscription?.current_period_end)}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Price</p>
                <p className="font-medium text-stone-900">
                  ${subscription?.billing_cycle === 'yearly' 
                    ? plan?.pricing?.yearly_price 
                    : plan?.pricing?.monthly_price
                  }/{subscription?.billing_cycle === 'yearly' ? 'year' : 'month'}
                </p>
              </div>
            </div>
          )}

          {/* Trial Warning */}
          {isTrialing && (
            <div className="flex items-start gap-3 p-4 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg mb-6">
              <AlertCircle className="w-5 h-5 text-[var(--gold)] shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[var(--gold)]">Trial Period Active</p>
                <p className="text-sm text-white/70">
                  Your trial ends on {formatDate(subscription?.trial_end)}. Add a payment method to continue using Pro features.
                </p>
              </div>
            </div>
          )}

          {/* Cancelled Warning */}
          {isCancelled && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-lg mb-6">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Subscription Cancelled</p>
                <p className="text-sm text-red-700">
                  You'll have access to {plan?.name} features until {formatDate(subscription?.current_period_end)}.
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {isFreePlan ? (
              <Link to="/pricing">
                <Button className="bg-primary hover:bg-primary/90">
                  Upgrade Plan <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            ) : isTrialing ? (
              <>
                <Link to="/pricing">
                  <Button className="bg-primary hover:bg-primary/90">
                    Add Payment Method
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                >
                  Cancel Trial
                </Button>
              </>
            ) : isCancelled ? (
              <Link to="/pricing">
                <Button className="bg-primary hover:bg-primary/90">
                  Resubscribe
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/pricing">
                  <Button variant="outline">
                    Change Plan
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Usage Section */}
        {usage && (
          <div className="bg-white border border-stone-200 rounded-xl p-8 mb-8">
            <h3 className="text-lg font-medium text-stone-900 mb-6">Usage This Month</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Boards Usage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-600">Project Boards</span>
                  <span className="text-sm font-medium">
                    {usage.boards.used} / {usage.boards.limit === 999999 ? '∞' : usage.boards.limit}
                  </span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      usage.boards.limit !== 999999 && usage.boards.used >= usage.boards.limit 
                        ? 'bg-red-500' 
                        : 'bg-primary'
                    }`}
                    style={{ 
                      width: usage.boards.limit === 999999 
                        ? '10%' 
                        : `${Math.min(100, (usage.boards.used / usage.boards.limit) * 100)}%` 
                    }}
                  />
                </div>
              </div>

              {/* Visual Searches Usage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-stone-600">Visual Searches</span>
                  <span className="text-sm font-medium">
                    {usage.visual_searches.used} / {usage.visual_searches.limit === 999999 ? '∞' : usage.visual_searches.limit}
                  </span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      usage.visual_searches.limit !== 999999 && usage.visual_searches.used >= usage.visual_searches.limit 
                        ? 'bg-red-500' 
                        : 'bg-primary'
                    }`}
                    style={{ 
                      width: usage.visual_searches.limit === 999999 
                        ? '10%' 
                        : `${Math.min(100, (usage.visual_searches.used / usage.visual_searches.limit) * 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Upgrade Prompt if near limit */}
            {!isFreePlan ? null : (
              (usage.boards.used >= usage.boards.limit * 0.8 || usage.visual_searches.used >= usage.visual_searches.limit * 0.8) && (
                <div className="mt-6 p-4 bg-amber-50 border border-amber-100 rounded-lg">
                  <p className="text-sm text-amber-800">
                    You're approaching your plan limits. 
                    <Link to="/pricing" className="font-medium underline ml-1">Upgrade to Pro</Link> for unlimited access.
                  </p>
                </div>
              )
            )}
          </div>
        )}

        {/* Features Section */}
        <div className="bg-white border border-stone-200 rounded-xl p-8 mb-8">
          <h3 className="text-lg font-medium text-stone-900 mb-6">Plan Features</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plan?.features?.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-stone-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Billing History */}
        {billingHistory.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-xl p-8">
            <h3 className="text-lg font-medium text-stone-900 mb-6">Billing History</h3>
            
            <div className="divide-y divide-stone-100">
              {billingHistory.map((transaction) => (
                <div key={transaction.transaction_id} className="py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-stone-900">
                      {transaction.metadata?.plan_name || transaction.plan_id} - {transaction.billing_cycle}
                    </p>
                    <p className="text-sm text-stone-500">{formatDate(transaction.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-stone-900">${transaction.amount}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      transaction.payment_status === 'paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {transaction.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SubscriptionManagement;
